import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@heroui/react';

const ACTION_OPTIONS = [
  { key: '', label: 'All actions' },
  { key: 'login', label: 'Login' },
  { key: 'logout', label: 'Logout' },
  { key: 'view', label: 'View' },
  { key: 'list', label: 'List' },
  { key: 'create', label: 'Create' },
  { key: 'edit', label: 'Edit' },
  { key: 'update', label: 'Update' },
  { key: 'publish', label: 'Publish' },
  { key: 'rollback', label: 'Rollback' },
  { key: 'delete', label: 'Delete' },
];

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [username, setUsername] = useState('');
  const [action, setAction] = useState('');
  const [searchUsername, setSearchUsername] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '50',
      });

      if (searchUsername) params.append('username', searchUsername);
      if (action) params.append('action', action);

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/admin/activity-logs?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwt')}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        setLogs(result.data || []);
        setTotalPages(result.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchUsername, action]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchUsername(username.trim());
  };

  return (
    <div className="px-4">
      <div className="flex flex-col gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Activity Logs</h1>

        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          <Input
            label="Search user"
            placeholder="Username or name"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="max-w-xs"
            variant="bordered"
          />
          <Select
            label="Action"
            selectedKeys={[action]}
            onSelectionChange={(keys) => setAction(Array.from(keys)[0] || '')}
            className="max-w-xs"
            variant="bordered"
          >
            {ACTION_OPTIONS.map((item) => (
              <SelectItem key={item.key} value={item.key}>
                {item.label}
              </SelectItem>
            ))}
          </Select>
          <Button type="submit" className="bg-admin_black text-white">
            Search
          </Button>
          <Button
            type="button"
            variant="bordered"
            onClick={() => {
              setUsername('');
              setSearchUsername('');
              setAction('');
              setPage(1);
            }}
          >
            Reset
          </Button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <Table aria-label="Activity logs table" className="bg-white rounded-lg">
            <TableHeader>
              <TableColumn>Time</TableColumn>
              <TableColumn>User</TableColumn>
              <TableColumn>Action</TableColumn>
              <TableColumn>Description</TableColumn>
            </TableHeader>
            <TableBody emptyContent="No activity logs found">
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{formatDate(log.created_at)}</TableCell>
                  <TableCell>{log.user_name || log.username || '-'}</TableCell>
                  <TableCell className="capitalize">{log.action}</TableCell>
                  <TableCell>{log.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex justify-between items-center mt-4">
            <Button
              variant="bordered"
              isDisabled={page <= 1}
              onPress={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="bordered"
              isDisabled={page >= totalPages}
              onPress={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityLogs;
